import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { k8sResourceService } from '@/services/k8sResourceService'
import SearchableSelector from '@/components/ui/SearchableSelector'

interface EnvVariable {
  id: string
  key: string
  value: string
}

interface ConfigMapRef {
  id: string
  envName: string
  configMapName: string
  configMapKey: string
  optional: boolean
}

interface SecretRef {
  id: string
  envName: string
  secretName: string
  secretKey: string
  optional: boolean
}

interface EnvConfigEditorProps {
  flow: any
  canEdit: boolean
  onSave: (updatedManifest: any, configName: string) => void
}

export default function EnvConfigEditor({ flow, canEdit, onSave }: EnvConfigEditorProps) {
  const { t } = useTranslation(['pipeline-studio', 'common'])
  const [isEditing, setIsEditing] = useState(false)

  // Edit state
  const [envVariables, setEnvVariables] = useState<EnvVariable[]>([])
  const [configMapRefs, setConfigMapRefs] = useState<ConfigMapRef[]>([])
  const [secretRefs, setSecretRefs] = useState<SecretRef[]>([])

  // Resources state
  const [configMaps, setConfigMaps] = useState<string[]>([])
  const [configMapsLoading, setConfigMapsLoading] = useState(false)
  const [secrets, setSecrets] = useState<string[]>([])
  const [secretsLoading, setSecretsLoading] = useState(false)

  // Fetch ConfigMaps when editing
  useEffect(() => {
    const fetchConfigMaps = async () => {
      if (!isEditing || !flow.metadata?.namespace) return

      setConfigMapsLoading(true)
      try {
        const data = await k8sResourceService.getConfigMaps(flow.metadata.namespace)
        const names = k8sResourceService.extractConfigMapNames(data)
        setConfigMaps(names)
      } catch (error) {
        console.error('Failed to fetch ConfigMaps:', error)
        setConfigMaps([])
      } finally {
        setConfigMapsLoading(false)
      }
    }

    fetchConfigMaps()
  }, [isEditing, flow.metadata?.namespace])

  // Fetch Secrets when editing
  useEffect(() => {
    const fetchSecrets = async () => {
      if (!isEditing || !flow.metadata?.namespace) return

      setSecretsLoading(true)
      try {
        const data = await k8sResourceService.getSecrets(flow.metadata.namespace)
        const names = k8sResourceService.extractSecretNames(data)
        setSecrets(names)
      } catch (error) {
        console.error('Failed to fetch Secrets:', error)
        setSecrets([])
      } finally {
        setSecretsLoading(false)
      }
    }

    fetchSecrets()
  }, [isEditing, flow.metadata?.namespace])

  // Convert flow env to editable format
  const convertToEditableFormat = () => {
    // Convert variables
    const variables = flow.spec?.env?.variables || {}
    const varsArray: EnvVariable[] = Object.entries(variables).map(([key, value], index) => ({
      id: `var-${Date.now()}-${index}`,
      key,
      value: String(value)
    }))
    setEnvVariables(varsArray)

    // Convert configMapRefs
    const cmRefs = flow.spec?.env?.configMapRefs || []
    const cmRefsArray: ConfigMapRef[] = cmRefs.map((ref: any, index: number) => ({
      id: `cm-${Date.now()}-${index}`,
      envName: ref.name || '',
      configMapName: ref.configMapRef?.name || '',
      configMapKey: ref.configMapRef?.key || '',
      optional: ref.configMapRef?.optional || false
    }))
    setConfigMapRefs(cmRefsArray)

    // Convert secretRefs
    const secRefs = flow.spec?.env?.secretRefs || []
    const secRefsArray: SecretRef[] = secRefs.map((ref: any, index: number) => ({
      id: `sec-${Date.now()}-${index}`,
      envName: ref.name || '',
      secretName: ref.secretRef?.name || '',
      secretKey: ref.secretRef?.key || '',
      optional: ref.secretRef?.optional || false
    }))
    setSecretRefs(secRefsArray)
  }

  const handleEdit = () => {
    convertToEditableFormat()
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleSave = () => {
    // Convert env variables to object
    const variables: Record<string, string> = {}
    envVariables.forEach(item => {
      if (item.key.trim()) {
        variables[item.key] = item.value
      }
    })

    // Convert configMapRefs
    const cmRefs = configMapRefs
      .filter(ref => ref.envName.trim() && ref.configMapName.trim() && ref.configMapKey.trim())
      .map(ref => ({
        name: ref.envName,
        configMapRef: {
          name: ref.configMapName,
          key: ref.configMapKey,
          optional: ref.optional
        }
      }))

    // Convert secretRefs
    const secRefs = secretRefs
      .filter(ref => ref.envName.trim() && ref.secretName.trim() && ref.secretKey.trim())
      .map(ref => ({
        name: ref.envName,
        secretRef: {
          name: ref.secretName,
          key: ref.secretKey,
          optional: ref.optional
        }
      }))

    // Build env config
    const envConfig: any = {}
    if (Object.keys(variables).length > 0) {
      envConfig.variables = variables
    }
    if (cmRefs.length > 0) {
      envConfig.configMapRefs = cmRefs
    }
    if (secRefs.length > 0) {
      envConfig.secretRefs = secRefs
    }

    const updatedFlow = {
      ...flow,
      spec: {
        ...flow.spec,
        env: Object.keys(envConfig).length > 0 ? envConfig : undefined
      }
    }

    if (!updatedFlow.spec.env) {
      delete updatedFlow.spec.env
    }

    onSave(updatedFlow, t('detail.envConfig'))
    setIsEditing(false)
  }

  // Environment Variables helpers
  const addEnvVariable = () => {
    setEnvVariables([...envVariables, {
      id: `var-${Date.now()}-${Math.random()}`,
      key: '',
      value: ''
    }])
  }

  const removeEnvVariable = (id: string) => {
    setEnvVariables(envVariables.filter(item => item.id !== id))
  }

  const updateEnvVariableKey = (id: string, newKey: string) => {
    setEnvVariables(envVariables.map(item =>
      item.id === id ? { ...item, key: newKey } : item
    ))
  }

  const updateEnvVariableValue = (id: string, newValue: string) => {
    setEnvVariables(envVariables.map(item =>
      item.id === id ? { ...item, value: newValue } : item
    ))
  }

  // ConfigMap Refs helpers
  const addConfigMapRef = () => {
    setConfigMapRefs([...configMapRefs, {
      id: `cm-${Date.now()}-${Math.random()}`,
      envName: '',
      configMapName: '',
      configMapKey: '',
      optional: false
    }])
  }

  const removeConfigMapRef = (id: string) => {
    setConfigMapRefs(configMapRefs.filter(item => item.id !== id))
  }

  const updateConfigMapRef = (id: string, field: keyof ConfigMapRef, value: any) => {
    setConfigMapRefs(configMapRefs.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ))
  }

  // Secret Refs helpers
  const addSecretRef = () => {
    setSecretRefs([...secretRefs, {
      id: `sec-${Date.now()}-${Math.random()}`,
      envName: '',
      secretName: '',
      secretKey: '',
      optional: false
    }])
  }

  const removeSecretRef = (id: string) => {
    setSecretRefs(secretRefs.filter(item => item.id !== id))
  }

  const updateSecretRef = (id: string, field: keyof SecretRef, value: any) => {
    setSecretRefs(secretRefs.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ))
  }

  const hasVariables = flow.spec?.env?.variables && Object.keys(flow.spec.env.variables).length > 0
  const hasConfigMapRefs = flow.spec?.env?.configMapRefs && Array.isArray(flow.spec.env.configMapRefs) && flow.spec.env.configMapRefs.length > 0
  const hasSecretRefs = flow.spec?.env?.secretRefs && Array.isArray(flow.spec.env.secretRefs) && flow.spec.env.secretRefs.length > 0
  const hasNoEnv = !hasVariables && !hasConfigMapRefs && !hasSecretRefs

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-text-dark">{t('detail.envConfig', 'Environment Variables')}</h2>
        {canEdit && !isEditing && (
          <button
            onClick={handleEdit}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-primary hover:bg-gray-50 rounded transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('detail.edit', 'Edit')}
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-6">
          {/* Direct Environment Variables */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="text-sm font-medium text-gray-900">
                  {t('detail.envVariables', 'Direct Variables')}
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Key-value pairs set directly as environment variables
                </p>
              </div>
              <button
                type="button"
                onClick={addEnvVariable}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary text-white rounded-md hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Add Variable
              </button>
            </div>

            {envVariables.length > 0 ? (
              <div className="space-y-2">
                {envVariables.map((item) => (
                  <div key={item.id} className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex gap-3 items-center">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={item.key}
                          onChange={(e) => updateEnvVariableKey(item.id, e.target.value)}
                          placeholder="VARIABLE_NAME"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono"
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={item.value}
                          onChange={(e) => updateEnvVariableValue(item.id, e.target.value)}
                          placeholder="value"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeEnvVariable(item.id)}
                        className="flex items-center justify-center w-9 h-9 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200"
                        title="Remove variable"
                      >
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                <p className="text-xs text-gray-500 text-center font-medium">No direct variables added</p>
                <p className="text-xs text-gray-400 text-center mt-1">Click "Add Variable" to get started</p>
              </div>
            )}
          </div>

          {/* ConfigMap References */}
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="text-sm font-medium text-gray-900">
                  {t('detail.envConfigMapRefs', 'From ConfigMaps')}
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Load environment variables from ConfigMap keys
                </p>
              </div>
              <button
                type="button"
                onClick={addConfigMapRef}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Add ConfigMap Ref
              </button>
            </div>

            {configMapRefs.length > 0 ? (
              <div className="space-y-3">
                {configMapRefs.map((item) => (
                  <div key={item.id} className="bg-white rounded-lg p-3 border border-blue-300">
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Env Variable Name</label>
                          <input
                            type="text"
                            value={item.envName}
                            onChange={(e) => updateConfigMapRef(item.id, 'envName', e.target.value)}
                            placeholder="DB_HOST"
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-200 font-mono"
                          />
                        </div>
                        <div>
                          <SearchableSelector
                            title="ConfigMap"
                            value={item.configMapName}
                            onChange={(val) => updateConfigMapRef(item.id, 'configMapName', val)}
                            options={configMaps}
                            placeholder="Select ConfigMap..."
                            loading={configMapsLoading}
                            disabled={false}
                            emptyMessage="No ConfigMaps available"
                            showCount={false}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Key</label>
                          <input
                            type="text"
                            value={item.configMapKey}
                            onChange={(e) => updateConfigMapRef(item.id, 'configMapKey', e.target.value)}
                            placeholder="database.host"
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-200 font-mono"
                          />
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={item.optional}
                              onChange={(e) => updateConfigMapRef(item.id, 'optional', e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-700">Optional</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeConfigMapRef(item.id)}
                          className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                        >
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 border border-dashed border-blue-300 rounded-lg bg-white">
                <p className="text-xs text-gray-500 text-center font-medium">No ConfigMap references added</p>
                <p className="text-xs text-gray-400 text-center mt-1">Click "Add ConfigMap Ref" to get started</p>
              </div>
            )}
          </div>

          {/* Secret References */}
          <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="text-sm font-medium text-gray-900">
                  {t('detail.envSecretRefs', 'From Secrets')}
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Load environment variables from Secret keys
                </p>
              </div>
              <button
                type="button"
                onClick={addSecretRef}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Add Secret Ref
              </button>
            </div>

            {secretRefs.length > 0 ? (
              <div className="space-y-3">
                {secretRefs.map((item) => (
                  <div key={item.id} className="bg-white rounded-lg p-3 border border-purple-300">
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Env Variable Name</label>
                          <input
                            type="text"
                            value={item.envName}
                            onChange={(e) => updateSecretRef(item.id, 'envName', e.target.value)}
                            placeholder="DB_PASSWORD"
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-200 font-mono"
                          />
                        </div>
                        <div>
                          <SearchableSelector
                            title="Secret"
                            value={item.secretName}
                            onChange={(val) => updateSecretRef(item.id, 'secretName', val)}
                            options={secrets}
                            placeholder="Select Secret..."
                            loading={secretsLoading}
                            disabled={false}
                            emptyMessage="No Secrets available"
                            showCount={false}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Key</label>
                          <input
                            type="text"
                            value={item.secretKey}
                            onChange={(e) => updateSecretRef(item.id, 'secretKey', e.target.value)}
                            placeholder="password"
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-200 font-mono"
                          />
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={item.optional}
                              onChange={(e) => updateSecretRef(item.id, 'optional', e.target.checked)}
                              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-700">Optional</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeSecretRef(item.id)}
                          className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                        >
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 border border-dashed border-purple-300 rounded-lg bg-white">
                <p className="text-xs text-gray-500 text-center font-medium">No Secret references added</p>
                <p className="text-xs text-gray-400 text-center mt-1">Click "Add Secret Ref" to get started</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={handleSave}
              className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t('detail.save', 'Save')}
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </div>
      ) : (
        <div>
          {/* Direct Variables Display */}
          {hasVariables && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('detail.envVariables', 'Direct Variables')}</h3>
              <div className="bg-gray-50 rounded p-3 space-y-2">
                {Object.entries(flow.spec.env.variables).map(([key, value]) => (
                  <div key={key} className="flex items-start border-b border-gray-200 pb-2 last:border-0 last:pb-0">
                    <span className="font-mono text-xs text-gray-600 font-semibold min-w-[120px]">{key}</span>
                    <span className="font-mono text-xs text-gray-900 break-all ml-3">{value as string}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ConfigMap References Display */}
          {hasConfigMapRefs && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('detail.envConfigMapRefs', 'From ConfigMaps')}</h3>
              <div className="space-y-3">
                {flow.spec.env.configMapRefs.map((ref: any, index: number) => (
                  <div key={index} className="bg-blue-50 border border-blue-200 rounded p-3">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="font-medium text-gray-600">{t('detail.envName', 'Env Name')}:</span>
                        <span className="ml-2 font-mono text-gray-900">{ref.name}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">{t('detail.envConfigMapName', 'ConfigMap')}:</span>
                        <span className="ml-2 font-mono text-gray-900">{ref.configMapRef?.name || '-'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">{t('detail.envKey', 'Key')}:</span>
                        <span className="ml-2 font-mono text-gray-900">{ref.configMapRef?.key || '-'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">{t('detail.envOptional', 'Optional')}:</span>
                        <span className="ml-2">
                          {ref.configMapRef?.optional ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              Yes
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              No
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Secret References Display */}
          {hasSecretRefs && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('detail.envSecretRefs', 'From Secrets')}</h3>
              <div className="space-y-3">
                {flow.spec.env.secretRefs.map((ref: any, index: number) => (
                  <div key={index} className="bg-purple-50 border border-purple-200 rounded p-3">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="font-medium text-gray-600">{t('detail.envName', 'Env Name')}:</span>
                        <span className="ml-2 font-mono text-gray-900">{ref.name}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">{t('detail.envSecretName', 'Secret')}:</span>
                        <span className="ml-2 font-mono text-gray-900">{ref.secretRef?.name || '-'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">{t('detail.envKey', 'Key')}:</span>
                        <span className="ml-2 font-mono text-gray-900">{ref.secretRef?.key || '-'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">{t('detail.envOptional', 'Optional')}:</span>
                        <span className="ml-2">
                          {ref.secretRef?.optional ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              Yes
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              No
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No env variables configured */}
          {hasNoEnv && (
            <div className="text-center text-sm text-gray-500 py-4">
              {t('detail.noEnvConfigured', 'No environment variables configured')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
